import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiFetch } from '../api';
import type { GpuServerImage } from '../types/models/GpuServer';
import type { ApiResponseGpuServerImages } from '../types/api/ApiResponseGpuServer';
import { jsendErrorMessage, jsendFieldErrors, readJsendBody } from './gpuServerResponse';

interface ImagesEntry {
  images: GpuServerImage[] | null;
  loading: boolean;
  error: string | null;
}

export interface UseGpuServerImagesReturn {
  /** The cached images of a product; `null` until fetched (or after a failure). */
  imagesFor: (productId: number) => GpuServerImage[] | null;
  /**
   * Fetch a product's images unless they are cached, already loading, or failed (a failure is not
   * retried implicitly). Pass `force` for a manual Retry.
   */
  fetchImages: (productId: number, force?: boolean) => Promise<void>;
  loadingFor: (productId: number) => boolean;
  errorFor: (productId: number) => string | null;
}

/**
 * `GET /datacenters/gpu-servers/offer/images?product_id=<id>` — the OS images of one product
 * (docs/api-types/gpu-servers.md). A LIVE upstream read: fetched lazily (on entering the wizard's
 * image step), once per product, and cached here — mount the hook above `<Wizard>` so the cache
 * survives step changes.
 */
export const useGpuServerImages = (): UseGpuServerImagesReturn => {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  const [entries, setEntries] = useState<Record<number, ImagesEntry>>({});
  // Mirrors `entries` synchronously, so two calls in one render don't both fetch.
  const entriesRef = useRef<Record<number, ImagesEntry>>({});

  const setEntry = useCallback((productId: number, entry: ImagesEntry) => {
    entriesRef.current = { ...entriesRef.current, [productId]: entry };
    setEntries(entriesRef.current);
  }, []);

  const fetchImages = useCallback(
    async (productId: number, force: boolean = false) => {
      const current = entriesRef.current[productId];
      if (current?.loading || (!force && current !== undefined)) {
        return;
      }
      setEntry(productId, { images: null, loading: true, error: null });
      try {
        const response = await apiFetchRef.current(`/datacenters/gpu-servers/offer/images?product_id=${productId}`);
        const body = await readJsendBody<ApiResponseGpuServerImages>(response);
        if (body?.status === 'success' && Array.isArray(body.data?.images)) {
          setEntry(productId, { images: body.data.images, loading: false, error: null });
          return;
        }
        const fieldError = jsendFieldErrors(body).product_id;
        setEntry(productId, {
          images: null,
          loading: false,
          error: fieldError ?? jsendErrorMessage(body, 'Could not load the system images of this server.'),
        });
      } catch (err) {
        setEntry(productId, {
          images: null,
          loading: false,
          error: err instanceof Error ? err.message : 'An error occurred while loading the system images.',
        });
      }
    },
    [setEntry],
  );

  const imagesFor = useCallback((productId: number) => entries[productId]?.images ?? null, [entries]);
  const loadingFor = useCallback((productId: number) => entries[productId]?.loading ?? false, [entries]);
  const errorFor = useCallback((productId: number) => entries[productId]?.error ?? null, [entries]);

  return { imagesFor, fetchImages, loadingFor, errorFor };
};

export default useGpuServerImages;
